(() => {
  'use strict';

  const namespace = '__masHomeBannerV1';
  if (window[namespace]) {
    window[namespace].mount(document);
    return;
  }

  const instances = new Map();
  const managerAbort = new AbortController();
  const restoreAttribute = (element, name, value) => {
    if (value === null) element.removeAttribute(name);
    else element.setAttribute(name, value);
  };

  class Banner {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-banner-track]');
      this.slides = Array.from(this.track.querySelectorAll('[data-banner-slide]'));
      this.controls = Array.from(root.querySelectorAll('[data-banner-controls]'));
      this.dots = Array.from(root.querySelectorAll('[data-banner-dot]'));
      this.status = root.querySelector('[data-banner-status]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.editorMode = Boolean(window.Shopify && window.Shopify.designMode);
      this.hovered = false;
      this.focusPaused = root.contains(document.activeElement);
      this.inView = true;
      this.autoplayTimer = null;
      this.abort = new AbortController();
      this.gestureAbort = null;
      this.gesture = null;
      this.suppressClickUntil = 0;
      this.destroyed = false;
      this.original = {
        tabindex: root.getAttribute('tabindex'),
        activeIndex: root.getAttribute('data-active-index'),
        transform: this.track.style.transform,
        touchAction: this.track.style.touchAction,
        userSelect: this.track.style.userSelect,
        ready: root.classList.contains('is-ready'),
        single: root.classList.contains('is-single'),
        status: this.status ? this.status.textContent : '',
        statusLive: this.status ? this.status.getAttribute('aria-live') : null,
        controls: this.controls.map((control) => control.hidden),
        slides: this.slides.map((slide) => ({
          hidden: slide.getAttribute('aria-hidden'),
          inert: slide.hasAttribute('inert'),
          active: slide.classList.contains('is-active')
        })),
        dots: this.dots.map((dot) => ({
          pressed: dot.getAttribute('aria-pressed'),
          current: dot.getAttribute('aria-current'),
          active: dot.classList.contains('is-active')
        }))
      };

      if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '0');
      this.track.style.touchAction = 'pan-y pinch-zoom';
      const requested = Number(root.dataset.activeIndex);
      this.index = Number.isInteger(requested)
        ? Math.max(0, Math.min(requested, this.slides.length - 1)) : 0;

      const on = (element, type, handler, options = {}) => {
        element.addEventListener(type, handler, { ...options, signal: this.abort.signal });
      };
      on(root, 'pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.scheduleAutoplay();
      });
      on(root, 'pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = false;
        this.scheduleAutoplay();
      });
      on(root, 'focusin', () => {
        this.focusPaused = true;
        this.scheduleAutoplay();
      });
      on(root, 'focusout', (event) => {
        if (event.relatedTarget && root.contains(event.relatedTarget)) return;
        this.focusPaused = false;
        this.scheduleAutoplay();
      });
      on(document, 'visibilitychange', () => this.scheduleAutoplay());
      on(this.motion, 'change', () => this.scheduleAutoplay());
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          this.inView = entries[0].isIntersecting;
          this.scheduleAutoplay();
        });
        this.visibilityObserver.observe(root);
      }
      root.querySelectorAll('[data-banner-prev]').forEach((button) => {
        on(button, 'click', (event) => {
          event.preventDefault();
          this.goTo(this.index - 1);
        });
      });
      root.querySelectorAll('[data-banner-next]').forEach((button) => {
        on(button, 'click', (event) => {
          event.preventDefault();
          this.goTo(this.index + 1);
        });
      });
      this.dots.forEach((dot) => {
        on(dot, 'click', (event) => {
          const index = Number(dot.dataset.slideIndex);
          if (!Number.isInteger(index) || index < 0 || index >= this.slides.length) return;
          event.preventDefault();
          this.goTo(index);
        });
      });
      on(root, 'keydown', (event) => {
        // Do not take arrow keys from links, buttons, forms or editable text.
        if (event.target !== root || event.defaultPrevented ||
            event.altKey || event.ctrlKey || event.metaKey || event.shiftKey ||
            this.slides.length < 2) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.goTo(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      });
      on(this.track, 'pointerdown', (event) => this.startGesture(event));
      on(this.track, 'dragstart', (event) => {
        if (this.gesture) event.preventDefault();
      });
      on(this.track, 'click', (event) => {
        // Keyboard/synthetic activation (detail === 0) is never swallowed.
        if (event.detail === 0 || performance.now() > this.suppressClickUntil) return;
        this.suppressClickUntil = 0;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, { capture: true });

      root.classList.toggle('is-single', this.slides.length < 2);
      this.controls.forEach((control) => { control.hidden = this.slides.length < 2; });
      this.render();
      root.classList.add('is-ready');
      this.scheduleAutoplay();
    }

    scheduleAutoplay() {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
      if (this.destroyed) return;
      const playing = this.slides.length > 1 && !this.editorMode &&
        !this.motion.matches && !this.hovered && !this.focusPaused && !this.gesture &&
        !document.hidden && this.inView;
      if (this.status) this.status.setAttribute('aria-live', playing ? 'off' : 'polite');
      if (playing) {
        this.autoplayTimer = setTimeout(() => {
          this.autoplayTimer = null;
          this.goTo(this.index + 1);
        }, 5000);
      }
    }

    focusRegion() {
      try { this.root.focus({ preventScroll: true }); }
      catch (_) { this.root.focus(); }
    }

    render() {
      // Move focus before applying inert, so no focused CTA becomes inaccessible.
      const focused = document.activeElement;
      if (focused && this.slides.some((slide, index) =>
        index !== this.index && slide.contains(focused))) this.focusRegion();

      this.track.style.transform = `translate3d(${-100 * this.index}%, 0, 0)`;
      this.root.dataset.activeIndex = String(this.index);
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        if (active) {
          const media = slide.querySelector('template[data-banner-media]');
          if (media) {
            const fragment = media.content.cloneNode(true);
            fragment.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
            media.replaceWith(fragment);
          }
        }
        slide.setAttribute('aria-hidden', String(!active));
        slide.toggleAttribute('inert', !active);
        slide.classList.toggle('is-active', active);
      });
      this.dots.forEach((dot) => {
        const active = Number(dot.dataset.slideIndex) === this.index;
        dot.setAttribute('aria-pressed', String(active));
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
        dot.classList.toggle('is-active', active);
      });
      if (this.status) {
        const text = `Banner ${this.index + 1} of ${this.slides.length}`;
        if (this.status.textContent !== text) this.status.textContent = text;
      }
    }

    goTo(index) {
      if (this.destroyed || !Number.isFinite(index)) return;
      const next = ((Math.trunc(index) % this.slides.length) + this.slides.length) % this.slides.length;
      if (next === this.index) {
        this.scheduleAutoplay();
        return;
      }
      this.endGesture(false);
      this.index = next;
      this.render();
      this.scheduleAutoplay();
    }

    startGesture(event) {
      if (this.slides.length < 2 || !event.isPrimary || event.button !== 0) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target && target.closest('input, textarea, select, option, [contenteditable]:not([contenteditable="false"]), [data-banner-no-swipe], [data-banner-controls]')) return;
      this.endGesture(false);
      this.suppressClickUntil = 0;
      this.gesture = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        deltaX: 0,
        horizontal: false,
        capture: false
      };
      this.scheduleAutoplay();
      this.gestureAbort = new AbortController();
      const options = { signal: this.gestureAbort.signal };
      window.addEventListener('pointermove', (move) => this.moveGesture(move), { ...options, passive: false });
      window.addEventListener('pointerup', (up) => {
        if (!this.gesture || up.pointerId !== this.gesture.pointerId) return;
        this.gesture.deltaX = up.clientX - this.gesture.startX;
        this.endGesture(true);
      }, options);
      window.addEventListener('pointercancel', (cancel) => {
        if (this.gesture && cancel.pointerId === this.gesture.pointerId) this.endGesture(false);
      }, options);
      window.addEventListener('blur', () => this.endGesture(false), options);
    }

    moveGesture(event) {
      const gesture = this.gesture;
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      const deltaX = event.clientX - gesture.startX;
      const deltaY = event.clientY - gesture.startY;
      const x = Math.abs(deltaX);
      const y = Math.abs(deltaY);
      if (!gesture.horizontal) {
        if (y > 10 && y > x * 1.15) {
          this.endGesture(false);
          return;
        }
        if (x < 10 || x <= y * 1.15) return;
        gesture.horizontal = true;
        this.root.classList.add('is-dragging');
        this.track.style.userSelect = 'none';
        try {
          this.track.setPointerCapture(event.pointerId);
          gesture.capture = true;
        } catch (_) { /* Window listeners also cover a pointer outside the track. */ }
      }
      gesture.deltaX = deltaX;
      if (event.cancelable) event.preventDefault();
    }

    endGesture(commit) {
      const gesture = this.gesture;
      this.gesture = null;
      if (this.gestureAbort) this.gestureAbort.abort();
      this.gestureAbort = null;
      this.root.classList.remove('is-dragging');
      this.track.style.userSelect = this.original.userSelect;
      if (!gesture) return;
      this.scheduleAutoplay();
      if (gesture.capture) {
        try { this.track.releasePointerCapture(gesture.pointerId); } catch (_) { /* Already released. */ }
      }
      if (!commit || !gesture.horizontal) return;
      this.suppressClickUntil = performance.now() + 600;
      const threshold = Math.max(36, Math.min(90, this.root.getBoundingClientRect().width * 0.12));
      if (Math.abs(gesture.deltaX) >= threshold) {
        this.goTo(this.index + (gesture.deltaX < 0 ? 1 : -1));
      }
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      clearTimeout(this.autoplayTimer);
      this.visibilityObserver?.disconnect();
      this.endGesture(false);
      this.abort.abort();
      restoreAttribute(this.root, 'tabindex', this.original.tabindex);
      restoreAttribute(this.root, 'data-active-index', this.original.activeIndex);
      this.root.classList.toggle('is-ready', this.original.ready);
      this.root.classList.toggle('is-single', this.original.single);
      this.track.style.transform = this.original.transform;
      this.track.style.touchAction = this.original.touchAction;
      this.controls.forEach((control, index) => { control.hidden = this.original.controls[index]; });
      this.slides.forEach((slide, index) => {
        const original = this.original.slides[index];
        restoreAttribute(slide, 'aria-hidden', original.hidden);
        slide.toggleAttribute('inert', original.inert);
        slide.classList.toggle('is-active', original.active);
      });
      this.dots.forEach((dot, index) => {
        const original = this.original.dots[index];
        restoreAttribute(dot, 'aria-pressed', original.pressed);
        restoreAttribute(dot, 'aria-current', original.current);
        dot.classList.toggle('is-active', original.active);
      });
      if (this.status) {
        this.status.textContent = this.original.status;
        restoreAttribute(this.status, 'aria-live', this.original.statusLive);
      }
    }
  }

  function mount(scope) {
    for (const [root, instance] of instances) {
      if (!root.isConnected) {
        instance.destroy();
        instances.delete(root);
      }
    }
    const roots = Array.from(scope.querySelectorAll('[data-mas-banner]'));
    if (scope instanceof Element && scope.matches('[data-mas-banner]')) roots.unshift(scope);
    roots.forEach((root) => {
      if (instances.has(root)) return;
      const track = root.querySelector('[data-banner-track]');
      if (!track || !track.querySelector('[data-banner-slide]')) return;
      instances.set(root, new Banner(root));
    });
  }

  function unmount(scope) {
    for (const [root, instance] of instances) {
      if (root === scope || scope.contains(root)) {
        instance.destroy();
        instances.delete(root);
      }
    }
  }

  window[namespace] = { mount };
  const managerOptions = { signal: managerAbort.signal };
  document.addEventListener('shopify:section:load', (event) => mount(event.target), managerOptions);
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target), managerOptions);
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail && event.detail.blockId;
    if (!blockId) return;
    mount(document);
    for (const instance of instances.values()) {
      const index = instance.slides.findIndex((slide) => slide.dataset.blockId === String(blockId));
      if (index !== -1) {
        instance.goTo(index);
        break;
      }
    }
  }, managerOptions);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(document), { ...managerOptions, once: true });
  }
  mount(document);
})();
